import type { ReactNode } from 'react';
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { cx } from './cx.js';

/**
 * A white chip across the top of a screen: something went wrong, or something
 * is still happening.
 *
 * Twenty of these were written out by hand in twelve files, and the copies had
 * drifted in the one way that matters. Six had lost `role="alert"`, so the
 * message appeared on screen and was never announced — a player using a screen
 * reader was told nothing at all. That is what a copy-pasted banner costs, and
 * it is why the role is a prop with a default rather than something each screen
 * remembers.
 *
 * `status` is the quieter register: a wait, a reconnect, a ticket that expired.
 * It is polite, so it does not interrupt what is being read.
 *
 * Both registers are the same chip. The alert stands on the error surface and
 * leads with a red warning mark; the words are still ink, because ink on
 * `red-muted` reads calmly and a solid red field would make every failure
 * shout. The mark is a filled red disc with the exclamation punched out in
 * paper — red at the size the brand allows it, carrying a glyph anyone reads
 * as trouble before they read the sentence. It replaced a bare red tick, which
 * was the brand's marking device doing a job it could not do: a tick says
 * "this row is theirs", not "this went wrong". Before that the accent was a
 * thick red left border, and the chip's rounded corner clipped it into a
 * crescent sliver that read as a rendering bug.
 *
 * The third register is `good`, a status with a check: something the player
 * wanted has happened. The chip stands on the teal wash and leads with a
 * filled teal check, a glyph accent under color rule 4 rather than a field.
 * Chosen by Mitch on 2026-09-22 from five candidates on the Account Settings
 * sketch, where the plain status chip read as too small a thing for the
 * moment after signing up. It is a `tone` of `status`, not a role of its own:
 * a success never interrupts, so the type will not let a good chip be an alert.
 */

interface BannerBase {
  /** For a field that points at this banner with `aria-describedby`. */
  readonly id?: string | undefined;
  /** Layout and placement only. The chip is not yours. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

export type BannerTone =
  /** The white chip. Waiting, a reconnect, a seat that opened. */
  | 'plain'
  /** Something the player wanted has happened. Teal wash, filled check. */
  | 'good';

export type BannerProps = BannerBase &
  (
    | {
        /**
         * `alert` interrupts, `status` waits its turn. Default to `alert`: a
         * banner that is only telling you something can say so, but a failure
         * that is not announced is a failure nobody hears.
         */
        readonly role?: 'alert' | undefined;
        readonly tone?: undefined;
      }
    | {
        readonly role: 'status';
        /** Which status this is. `plain` unless the news is good. */
        readonly tone?: BannerTone | undefined;
      }
  );

const BANNER =
  'flex items-center gap-2 self-center rounded-2xl border-2 border-ink px-4 py-2 text-ink shadow-xs shadow-ink';

/** The surface, and nothing else in the class list, changes with the register. */
const FACE = {
  alert: 'bg-red-muted',
  plain: 'bg-ground-light',
  good: 'bg-teal-muted',
} satisfies Record<'alert' | BannerTone, string>;

/**
 * The alert's glyph: an exclamation punched out of a red disc.
 *
 * A Phosphor warning circle keeps this generic UI mark consistent with every
 * other client control. The chip centers it: a banner carries one short
 * sentence, so the mark and the words sit on one axis.
 */
function WarningMark(): React.JSX.Element {
  return (
    <WarningCircleIcon
      aria-hidden="true"
      className="size-5 flex-none text-red"
      focusable="false"
      weight="fill"
    />
  );
}

/** The good register's glyph: a filled check in the teal word stop. */
function GoodMark(): React.JSX.Element {
  return (
    <CheckCircleIcon
      aria-hidden="true"
      className="size-5 flex-none text-teal-text"
      focusable="false"
      weight="fill"
    />
  );
}

export function Banner({
  role = 'alert',
  tone,
  id,
  className,
  children,
}: BannerProps): React.JSX.Element {
  const register = role === 'alert' ? 'alert' : (tone ?? 'plain');
  return (
    <p className={cx(BANNER, FACE[register], className)} id={id} role={role}>
      {register === 'alert' && <WarningMark />}
      {register === 'good' && <GoodMark />}
      <span className="min-w-0">{children}</span>
    </p>
  );
}
